import React from 'react';
import { translate } from 'react-jhipster';
import Box from '@mui/material/Box';
import FormControlLabel from '@mui/material/FormControlLabel';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Switch from '@mui/material/Switch';
import Typography from '@mui/material/Typography';

export type RebateLetterCategory =
  | 'REBATE_SHMOKH'
  | 'REBATE_BANK_A'
  | 'REBATE_THIRD_PARTY'
  | 'REBATE_BANK_A_THIRD_PARTY'
  | 'REBATE_CANCELLATION';

// Order matches the "3. LETTER FOR REBATE" mockup. Only one may be selected at a time.
const LETTER_CATEGORIES: Array<[RebateLetterCategory, string]> = [
  ['REBATE_SHMOKH', 'rebateShmokh'],
  ['REBATE_BANK_A', 'rebateBankA'],
  ['REBATE_THIRD_PARTY', 'rebateThirdParty'],
  ['REBATE_BANK_A_THIRD_PARTY', 'rebateBankAThirdParty'],
  ['REBATE_CANCELLATION', 'rebateCancellation'],
];

// Letter is optional (ALSHMOKH-59), like Reports and Document: a category/language is only required while enabled.
export const isLetterSettingValid = (enabled: boolean, value: string) => !enabled || value !== '';

// Email template language for the letter (ALSHMOKH-31). Defaults to English when a bank hasn't saved one yet.
export type RebateLetterLanguage = 'EN' | 'AR';

const LETTER_LANGUAGES: Array<[RebateLetterLanguage, string]> = [
  ['EN', 'english'],
  ['AR', 'arabic'],
];

interface LetterSectionProps {
  enabled: boolean;
  onEnabledChange: (enabled: boolean) => void;
  value: string;
  onChange: (value: string) => void;
  showErrors: boolean;
  bankName: string;
  language: string;
  onLanguageChange: (value: string) => void;
}

export const LetterSection = ({
  enabled,
  onEnabledChange,
  value,
  onChange,
  showErrors,
  bankName,
  language,
  onLanguageChange,
}: LetterSectionProps) => {
  const error = showErrors && !isLetterSettingValid(enabled, value);

  return (
    <Box sx={{ maxWidth: { md: '66%' } }}>
      <FormControlLabel
        control={<Switch checked={enabled} onChange={e => onEnabledChange(e.target.checked)} />}
        label={translate('comexApp.bank.rebate.letter.enable')}
      />
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {translate(enabled ? 'comexApp.bank.rebate.letter.enabledHint' : 'comexApp.bank.rebate.letter.disabledHint')}
      </Typography>

      {enabled && (
        <>
          <RadioGroup value={value} onChange={e => onChange(e.target.value)}>
            {LETTER_CATEGORIES.map(([category, labelKey]) => (
              <FormControlLabel
                key={category}
                value={category}
                control={<Radio />}
                label={translate(`comexApp.bank.rebate.letter.options.${labelKey}`, { bankName })}
              />
            ))}
          </RadioGroup>
          {error && (
            <Typography variant="caption" color="error" sx={{ mx: 1.75 }}>
              {translate('comexApp.bank.rebate.letter.required')}
            </Typography>
          )}

          <Typography variant="subtitle2" sx={{ mt: 3, mb: 1 }}>
            {translate('comexApp.bank.rebate.letter.languageLabel')}
          </Typography>
          <RadioGroup row value={language || 'EN'} onChange={e => onLanguageChange(e.target.value)}>
            {LETTER_LANGUAGES.map(([lang, labelKey]) => (
              <FormControlLabel
                key={lang}
                value={lang}
                control={<Radio />}
                label={translate(`comexApp.bank.rebate.letter.language.${labelKey}`)}
              />
            ))}
          </RadioGroup>
        </>
      )}
    </Box>
  );
};
